import React, {useState} from 'react'
import {toast} from 'react-toastify'
import {Header, Radio, ParagraphText, Toggle} from '@thryvlabs/maverick'
import {
  CatalogSectionContainer,
  PlanOptionContainer,
} from '../ui/command-center-layout'
import {currencyFormat, formatPrice} from '../../utils/currency-format'
import {
  BC_PLANS,
  MC_PLANS,
  KEAP_FAMILY_CODES,
  FREE_ACCOUNT_CODES,
  MC_2_0_PLANS,
  MC_2_0_SIGPREMIERPLANS,
  MC_TRIAL_PLAN_CODES,
  GEO_ADDON_CODES,
  GROWTH_PACKAGES,
} from '../../utils/constants'
import {useUserFeatures} from '../../context/user-features-context'
import {
  TGP_FOUNDATION_CODE,
  isThryvBasicCode,
  isTgpFoundationFamilyCode,
  isTgpFoundationBlockingCode,
  isTgpFamilyCode,
  isTgpStarterCode,
  isTgpSignatureCode,
  isTgpFoundationCatalogEnabled,
  isThryvBasicCatalogEnabled,
} from '../../utils/tgp-catalog-flags'
import {useUserRoles} from '../../utils/use-user-roles'
import {getAuthByRole} from '../../utils/get-screen-auth-by-role'
import {
  // commandCenterIcon,
  // businessCenterIconNew,
  // marketingCenterIconNew,
  // workforceCenterIcon,
  // commandCenterIconV1,
  keapMaxIcon,
  reverseGreenPlusIcon,
  reverseOrangePlusIcon,
  workforceCenterIconv1,
  marketingCenterIconv1,
  businessCenterIconv1,
  thryvSucessServicesIcon,
  adsBoostIcon,
  brandBoostIcon,
  socialBoostIcon,
  searchBoostIcon,
  thryvGrowthPlatformIcon,
} from '../../assets'

export const GROUP_ICONS = [
  {
    name: 'Business Center',
    img: businessCenterIconv1,
  },
  {
    name: 'Marketing Center',
    img: marketingCenterIconv1,
  },
  {
    name: 'Thryv Growth Platform',
    img: thryvGrowthPlatformIcon,
  },
  {
    name: 'Workforce Center',
    img: workforceCenterIconv1,
  },
  {
    name: 'Keap',
    img: keapMaxIcon,
  },
  {
    name: 'Home Services',
    img: keapMaxIcon,
  },
  {
    name: 'Thryv Add-Ons',
    img: reverseOrangePlusIcon,
  },
  {
    name: 'Ads Boost',
    img: adsBoostIcon,
  },
  {
    name: 'Brand Boost',
    img: brandBoostIcon,
  },
  {
    name: 'Social Boost',
    img: socialBoostIcon,
  },
  {
    name: 'Search Boost',
    img: searchBoostIcon,
  },
  {
    name: 'Thryv Success Services',
    img: thryvSucessServicesIcon,
  },
  {
    name: 'Keap Add-Ons',
    img: reverseGreenPlusIcon,
  },
  {
    name: 'Partner Certification',
    img: keapMaxIcon,
  },
]

const LISTING_REFRESH_CODE = 'addon-5-listrefrsh-m2m'
const LEADS_LIFT_CODE = 'addon-5-leadslift-m2m'
// FULSAS-12019: 'nzsales' alone missed users assigned 'NewZealandSales'
// (normalizes to 'newzealandsales' via getAuthByRole). Both are listed to
// match either Auth0 assignment. Must match ooe-service's
// cc-order-service.js LEADS_LIFT_ELIGIBLE_ROLES.
const LEADS_LIFT_ELIGIBLE_ROLES = [
  'admin',
  'premisepilot',
  'inside',
  'nzsales',
  'newzealandsales',
]
const BC_6_CODE = 'bc-6-thryv-m2m'
const MC_CENTER_CODES = [
  'mc_plus-5-thryv-m2m',
  'mc_pro-5-thryv-m2m',
  'mc_pro_enh-5-thryv-m2m',
  ...MC_TRIAL_PLAN_CODES,
]
const OTHER_BC_CODES = [
  'bc_plus-5-thryv-m2m',
  'bc_pro-5-thryv-m2m',
  'bc_unlm-5-thryv-m2m',
  'bc_ent-6-thryv-m2m',
  'bc_ent-unlm-5-thryv-m2m',
]

const normalizeKeyPart = value =>
  String(value || '')
    .replace(/\s/g, '')
    .toLowerCase()

const parseRestrictedGroupsFromFeatures = ({
  userFeatures = {},
  selectedCountry,
  roles = [],
}) => {
  const restrictions = new Set()
  if (!selectedCountry || !userFeatures) return restrictions

  const normalizedCountry = normalizeKeyPart(selectedCountry)
  const normalizedRoles = roles.map(role => normalizeKeyPart(role))

  Object.entries(userFeatures).forEach(([featureName, isEnabled]) => {
    if (isEnabled !== true) return
    if (typeof featureName !== 'string') return

    const matched = featureName.match(
      /^([A-Za-z]{2})_([^_]+)_([^_]+)_restrict$/,
    )
    if (!matched) return

    const [, featureCountry, featureGroup, featureRole] = matched
    if (normalizeKeyPart(featureCountry) !== normalizedCountry) return

    const normalizedFeatureRole = normalizeKeyPart(featureRole)
    if (!normalizedRoles.includes(normalizedFeatureRole)) return
    restrictions.add(normalizeKeyPart(featureGroup))
  })

  return restrictions
}

const isGroupRestrictedByFeature = (groupTitle, restrictedGroups) => {
  if (!groupTitle || !restrictedGroups?.size) return false
  return restrictedGroups.has(normalizeKeyPart(groupTitle))
}

const checkIfAvailableForUpgrade = ({itemsInCart, allItems, name}) => {
  const currentIndex = allItems.findIndex(
    ({name: itemName}) => itemName === name,
  )
  const centerType = name.split(' ')[0]
  const isCenterTypeAlreadyIncluded = itemsInCart.find(({name}) =>
    name.includes(centerType),
  )
  const alreadyIncludedCenterIndex = allItems.findIndex(
    ({name}) => isCenterTypeAlreadyIncluded?.name === name,
  )

  const mcUnavailableForUpgrade =
    itemsInCart.some(({code}) =>
      ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
        code,
      ),
    ) && name.includes('Marketing')
  return mcUnavailableForUpgrade
    ? false
    : currentIndex >= alreadyIncludedCenterIndex
}

const isRequiredFlagEnabled = (userFeatures, requiredFlags = []) => {
  if (!requiredFlags.length) return true
  return requiredFlags.every(flag => userFeatures[flag] || false)
}

const isRestrictFlagEnabled = (userFeatures, restrictFlags = []) => {
  if (!restrictFlags.length) return false
  return restrictFlags.some(flag => userFeatures[flag] || false)
}

console.log('isRestrictFlagEnabled :: ', isRestrictFlagEnabled)

const checkIfPlanIsUnavailableForRole = ({
  planType,
  name,
  centersAndAppsInCart,
  hasBusinessCenterAccess,
  hasMarketingCenterAccess,
}) => {
  if (planType !== 'center') return false

  const plans = ['marketing', 'business']
  const nameLower = name.toLowerCase()
  const isBCorMC = plans.find(plan => nameLower.includes(plan))

  if (!isBCorMC) return false
  if (isBCorMC) {
    if (nameLower.includes('business')) {
      if (!hasBusinessCenterAccess) {
        const isAlreadyIncluded = centersAndAppsInCart.some(({name}) =>
          name.toLowerCase().includes(isBCorMC),
        )
        return !isAlreadyIncluded
      } else {
        return false
      }
    } else if (nameLower.includes('marketing')) {
      if (!hasMarketingCenterAccess) {
        const isAlreadyIncluded = centersAndAppsInCart.some(({name}) =>
          name.toLowerCase().includes(isBCorMC),
        )
        return !isAlreadyIncluded
      } else {
        return false
      }
    }
  }
}

const checkIfAppIsUnavailableForRole = ({
  planType,
  name,
  code,
  centersAndAppsInCart,
  hasKeapMaxAccess,
  hasKeapHVACAccess,
}) => {
  const isKeapFamilyCode = KEAP_FAMILY_CODES.includes(code)
  if (planType !== 'app' && !isKeapFamilyCode && code != 'hvac-5-thryv-m2m')
    return false
  const nameLower = name.toLowerCase()
  if (isKeapFamilyCode || nameLower.includes('max')) {
    if (!hasKeapMaxAccess) {
      const isAlreadyIncluded = centersAndAppsInCart.some(
        ({code: itemCode, name: itemName}) =>
          KEAP_FAMILY_CODES.includes(itemCode) ||
          itemName?.toLowerCase().includes('max'),
      )
      return !isAlreadyIncluded
    } else {
      return false
    }
  } else if (
    [
      'maxhvac-5-thryv-m2m',
      'maxroof-5-thryv-m2m',
      'maxelect-5-thryv-m2m',
      'maxplumb-5-thryv-m2m',
    ].includes(code)
  ) {
    const isKeapMaxInCart = centersAndAppsInCart.some(({code: itemCode}) =>
      KEAP_FAMILY_CODES.includes(itemCode),
    )
    if (!hasKeapHVACAccess || !isKeapMaxInCart) {
      const isAlreadyIncluded = centersAndAppsInCart.some(({code: itemCode}) =>
        [
          'maxhvac-5-thryv-m2m',
          'maxroof-5-thryv-m2m',
          'maxelect-5-thryv-m2m',
          'maxplumb-5-thryv-m2m',
        ].includes(itemCode),
      )
      return !isAlreadyIncluded
    } else {
      return false
    }
  } else {
    return false
  }
}

/**
 *
 * @param {string | undefined} needFlag
 * @param {Record<string, true | undefined>} userFeatures
 * @returns {boolean}
 */
const checkIfHasNeededFlag = (needFlag, userFeatures) => {
  if (!needFlag) return true
  return userFeatures[needFlag] || false
}

const checkIfExcluded = ({
  itemsInCart,
  isItemAvailableForUpgrade,
  itemCode,
}) => {
  const isExcluded = itemsInCart.some(
    ({code: cartItemCode, excludes, entitlements}) =>
      // TGP-DOWNGRADE #5 (to revert: delete the next `!(...) &&` line and
      // restore the destructure to `({excludes, entitlements}) =>`).
      // A TGP plan in the cart or on the account must not grey out another
      // TGP plan (incl. TGP Foundation / Thryv Basic).
      !(isTgpFamilyCode(itemCode) && isTgpFamilyCode(cartItemCode)) &&
      (excludes?.includes(itemCode) ||
        entitlements?.some(({excludes}) => excludes?.includes(itemCode))),
  )

  return isExcluded && !isItemAvailableForUpgrade
}

function PlanOption({
  name,
  amount,
  img,
  entitlements,
  alternatePlanCodes,
  selectedPlan,
  setSelectedPlan,
  groupTitle,
  id,
  excludes,
  requiresCenter,
  planType,
  code,
  centersAndAppsInCart,
  defaultCentersAndApps,
  setSelectedOptions,
  setQuantity,
  tier,
  maxQty,
  isUpgrade,
  centers,
  isSEOMBGDisabled,
  setIsWebsiteSettingsModalVisible,
  hasBusinessCenterAccess,
  hasMarketingCenterAccess,
  hasKeapMaxAccess,
  hasKeapHVACAccess,
  hasCustomDataFYAccess,
  // hasCustomDataServiceAccess,
  subtitle,
  isNewTLMCAccessible,
  accountSubscriptionData,
  keapAccount,
  autoAddCenters,
  disableApps,
  disableCenters,
  isDisabled: isDisabledProp = false,
  prereqMessage,
  selectedCountry,
  parentProduct,
  productRank,
  productBillingType,
  downgrades,
  keapAddUsersRecurlyQty = 0,
  keapAddUsersRecurlyQtyAnnual = 0,
  ctnRecurlyQty = 0,
  ctnRecurlyQtyAnnual = 0,
  quantity = 1,
  kccRecurlyOption,
}) {
  const userFeatures = useUserFeatures()
  const {
    Enable_Social_Ads_And_TL_With_BC,
    MC_AU,
    MC_NZ,
    MC_Downgrade_Access,
    Enable_TGP_Downgrade,
  } = userFeatures
  const isCenter =
    planType === 'center' &&
    (name.includes('Center') || name.includes('My Account'))
  const isItemAvailableForUpgrade =
    isCenter &&
    isUpgrade &&
    checkIfAvailableForUpgrade({
      itemsInCart: centersAndAppsInCart,
      allItems: centers,
      name,
    })
  const isFree =
    name.toLowerCase().includes('basic') &&
    !isThryvBasicCode(code) &&
    name.toLowerCase() != 'basic crm data import'
  const isBusinessCenterPlusAlreadyIncluded =
    name.toLowerCase().includes('business center plus') &&
    centersAndAppsInCart.some(item => item.code === 'bc_plus-5-thryv-m2m')
  const isCommandCenterPlusAlreadyIncluded =
    name.toLowerCase().includes('my account plus') &&
    centersAndAppsInCart.some(item => item.code === 'cc_plus-5-thryv-m2m')
  const isCommandCenterProAlreadyIncluded =
    name.toLowerCase().includes('my account pro') &&
    centersAndAppsInCart.some(item => item.code === 'cc_pro-5-thryv-m2m')
  const isMarketingCenterPlusAlreadyIncluded =
    name.toLowerCase().includes('marketing center plus') &&
    centersAndAppsInCart.some(item => item.code === 'mc_plus-5-thryv-m2m')
  const isExcluded = checkIfExcluded({
    itemsInCart: centersAndAppsInCart,
    isItemAvailableForUpgrade,
    itemCode: code,
  })
  const isAppAlreadyIncluded =
    code !== 'seobase-5-thryv-m2m' &&
    !MC_2_0_SIGPREMIERPLANS.includes(code) &&
    centersAndAppsInCart.some(({code: appCode, alreadyIncluded}) => {
      return appCode === code && alreadyIncluded
    })
  const isInCart = centersAndAppsInCart.some(
    ({code: itemCode, alreadyIncluded}) =>
      itemCode === code && !alreadyIncluded,
  )

  let isWorkforceCenterInCart = false
  if (
    code === 'wc_plus-5-thryv-m2m' &&
    centersAndAppsInCart.some(
      ({code: itemCode}) => itemCode === 'wc_plus-5-thryv-m2m',
    )
  ) {
    isWorkforceCenterInCart = true
  }

  let isWorkforceCenterAddon = false
  if (code === 'addon-5-pr-emp_wc' || code === 'addon-5-pr-con_wc') {
    isWorkforceCenterAddon = true
  }

  const isWebsiteAddedAsOption = centersAndAppsInCart.some(({entitlements}) =>
    entitlements?.some(({name}) => name === 'Professionally Designed Website'),
  )
  const isWebsiteAppUnavailable =
    name === 'Professionally Designed Website' && isWebsiteAddedAsOption
  const isSEOMBGUnavailable =
    isSEOMBGDisabled && name === 'SEO Money Back Guarantee'
  const isRoleUnableToSelectCenter = checkIfPlanIsUnavailableForRole({
    planType,
    name,
    centersAndAppsInCart,
    hasBusinessCenterAccess,
    hasMarketingCenterAccess,
    isNewTLMCAccessible,
  })
  const isRoleUnableToSelectApp = checkIfAppIsUnavailableForRole({
    planType,
    name,
    code,
    centersAndAppsInCart,
    hasKeapMaxAccess,
    hasKeapHVACAccess,
  })

  // const isTLMCAccess = checkIfTLMCAvailable({
  //   name,
  //   centersAndAppsInCart,
  //   isNewTLMCAccessible,
  // })
  let isMcaaddonInCart = false
  if (code === 'addon-5-mcstartgr' || code === 'addon-5-mcexpangr') {
    isMcaaddonInCart = centersAndAppsInCart.some(
      ({code: itemCode}) => itemCode === 'addon-5-mcexpangr',
    )
  }

  let isReportingCenter = false
  if (
    code === 'rc-5-thryv-m2m' ||
    code === 'rc_basic-5-thryv-m2m' ||
    code === 'rc_plus-5-thryv-m2m'
  ) {
    isReportingCenter = true
  }

  const isThryvHVACorMaxInDefault = defaultCentersAndApps.centers.some(
    ({code}) =>
      code.includes('hvac-5-thryv-m2m') || KEAP_FAMILY_CODES.includes(code),
  )

  const isThryvHVACorMaxInCart = centersAndAppsInCart.some(
    ({code}) =>
      code.includes('hvac-5-thryv-m2m') || KEAP_FAMILY_CODES.includes(code),
  )

  const isZapierInCart = centersAndAppsInCart.some(
    ({code}) => code === 'kp-zapier-750zaps-m2m',
  )

  const isZapier = code === 'kp-zapier-750zaps-m2m'

  const isZapierDisabled =
    isZapier &&
    (isThryvHVACorMaxInDefault || isZapierInCart || !isThryvHVACorMaxInCart)

  const isMCInCart = centersAndAppsInCart.some(
    ({code: itemCode}) =>
      itemCode === 'mc_plus-5-thryv-m2m' || itemCode === 'mc_pro-5-thryv-m2m',
  )

  const isPaidMCTrialInCart = centersAndAppsInCart.some(({code: itemCode}) =>
    MC_2_0_PLANS.includes(itemCode),
  )

  const isMCSigPremierInCart = centersAndAppsInCart.some(({code: itemCode}) =>
    MC_2_0_SIGPREMIERPLANS.includes(itemCode),
  )

  const isListingRefreshSelected = code === 'addon-5-listrefrsh-m2m'
  const isSocialContentCreationDefaulted = defaultCentersAndApps?.apps?.some(
    ({code}) => code === 'addon-5-stratcontent-m2m',
  )

  const isThryvSuccessServicesPlanDisabled =
    isListingRefreshSelected && isSocialContentCreationDefaulted

  const isKccDisabled =
    code === 'keap_kcc-cert-5-fee' &&
    centersAndAppsInCart.some(({code}) => code === 'keap_kcp-cert-5-fee')
  const isKcpDisabled =
    code === 'keap_kcp-cert-5-fee' &&
    centersAndAppsInCart.some(({code}) => code === 'keap_kcc-cert-5-fee')

  let isDisabled =
    isDisabledProp ||
    isFree ||
    isBusinessCenterPlusAlreadyIncluded ||
    isCommandCenterPlusAlreadyIncluded ||
    isCommandCenterProAlreadyIncluded ||
    isMarketingCenterPlusAlreadyIncluded ||
    isExcluded ||
    isInCart ||
    isRoleUnableToSelectCenter ||
    isWebsiteAppUnavailable ||
    isRoleUnableToSelectApp ||
    isSEOMBGUnavailable ||
    isAppAlreadyIncluded ||
    isMcaaddonInCart ||
    isWorkforceCenterInCart ||
    isWorkforceCenterAddon ||
    isReportingCenter ||
    isZapierDisabled ||
    isThryvSuccessServicesPlanDisabled ||
    isKccDisabled ||
    isKcpDisabled

  const isSEO = ['seombg-5-thryv-m2m', 'seo-5-thryv-m2m'].includes(code)
  const isThryvLeadsInCart = centersAndAppsInCart.find(item =>
    ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
      item.code,
    ),
  )
  const isKeapMaxInCart = centersAndAppsInCart.find(({code}) =>
    KEAP_FAMILY_CODES.includes(code),
  )
  const isHomeServicesInCart = centersAndAppsInCart.find(item =>
    [
      'maxhvac-5-thryv-m2m',
      'maxroof-5-thryv-m2m',
      'maxelect-5-thryv-m2m',
      'maxplumb-5-thryv-m2m',
    ].includes(item.code),
  )
  const isHomeServicesInDefault = defaultCentersAndApps?.apps?.some(({code}) =>
    [
      'maxhvac-5-thryv-m2m',
      'maxroof-5-thryv-m2m',
      'maxelect-5-thryv-m2m',
      'maxplumb-5-thryv-m2m',
    ].includes(code),
  )
  const isBCInCart = centersAndAppsInCart.some(({code: itemCode}) =>
    BC_PLANS.includes(itemCode),
  )

  const isTLAvailableWhenBCInCart = Boolean(
    isBCInCart && Enable_Social_Ads_And_TL_With_BC,
  )

  const isSocialAdsAvailableWhenBCInCart = Boolean(
    isBCInCart && Enable_Social_Ads_And_TL_With_BC,
  )

  const isMCProEnhancedInCart = centersAndAppsInCart.some(
    ({code: itemCode}) => itemCode === 'mc_pro_enh-5-thryv-m2m',
  )

  const isBCInDefault = defaultCentersAndApps.centers.some(({code}) =>
    BC_PLANS.includes(code),
  )
  const isMCInDefault = defaultCentersAndApps.centers.some(({code}) =>
    MC_PLANS.includes(code),
  )

  const isMCProInDefault = defaultCentersAndApps.centers.some(
    ({code}) => code === 'mc_pro-5-thryv-m2m',
  )

  const isSameKeapPlanInDefault = defaultCentersAndApps.centers.some(
    ({code: centerCode}) => centerCode === code,
  )
  const isMCFoundationInCart = centersAndAppsInCart.some(({code: itemCode}) =>
    isTgpFoundationFamilyCode(itemCode),
  )
  // FULSAS-13789: TGP Foundation / Thryv Basic are not applicable when a
  // legacy Marketing Center or Business Center plan is already on the account
  // or in the cart (unless the plan itself is already there, e.g. auto-added).
  if (
    isTgpFoundationFamilyCode(code) &&
    !isMCFoundationInCart &&
    ![...centersAndAppsInCart, ...defaultCentersAndApps.centers].every(
      ({code: c}) => !isTgpFoundationBlockingCode(c),
    )
  ) {
    isDisabled = true
  }
  // FULSAS-13789: Growth Packages are not allowed while TGP Foundation or
  // Thryv Basic is on the account or in the cart.
  if (
    GROWTH_PACKAGES.includes(code) &&
    [...centersAndAppsInCart, ...defaultCentersAndApps.centers].some(
      ({code: c}) => isTgpFoundationFamilyCode(c),
    ) &&
    !centersAndAppsInCart.some(({code: c}) => c === code) &&
    !(defaultCentersAndApps.apps ?? []).some(({code: c}) => c === code)
  ) {
    isDisabled = true
  }
  if (code.includes('bc_') && isKeapMaxInCart) {
    isDisabled = true
  }
  if (code.includes('bc_') && isHomeServicesInCart) {
    isDisabled = true
  }
  if (code === 'addon-6-mc-socialcontentgen-m2m') {
    const isBCUnlimitedInCenter = centersAndAppsInCart.some(
      ({code: itemCode}) => ['bc_unlm-5-thryv-m2m'].includes(itemCode),
    )
    if (isBCUnlimitedInCenter) {
      isDisabled = true
    }
  }

  if (
    code.includes('seobase-5-thryv-m2m') &&
    selectedCountry === 'US' &&
    !(
      isMCInCart ||
      isMCFoundationInCart ||
      isBCInDefault ||
      isMCProEnhancedInCart ||
      isMCSigPremierInCart
    )
  ) {
    isDisabled = true
  }
  if (
    code.includes('seobase-5-thryv-m2m') &&
    selectedCountry !== 'US' &&
    !(
      isMCInCart ||
      isMCProEnhancedInCart ||
      isMCFoundationInCart ||
      isMCSigPremierInCart
    )
  ) {
    isDisabled = true
  }
  if (code.includes('vert_svcs-5-thryv-m2m') && !isHomeServicesInDefault) {
    isDisabled = true
  }
  if (code.includes('proservices-m2m') && !(isBCInDefault || isMCInDefault)) {
    isDisabled = true
  }

  if (code === 'mc_freetrial-5-thryv-m2m') {
    return false
  }
  // FULSAS-13789: TGP Foundation / Thryv Basic are feature-flag gated.
  if (
    code === TGP_FOUNDATION_CODE &&
    !isTgpFoundationCatalogEnabled(userFeatures)
  )
    return false
  if (isThryvBasicCode(code) && !isThryvBasicCatalogEnabled(userFeatures))
    return false

  if (code === 'addon-ctn-bninat-m2m') {
    const isCtnAnnualInCart = centersAndAppsInCart.some(
      ({code: c}) => c === 'addon-ctn-bninat-annual',
    )
    const isCtnAnnualInDefault = defaultCentersAndApps?.apps?.some(
      ({code: c}) => c === 'addon-ctn-bninat-annual',
    )
    isDisabled = isCtnAnnualInCart || isCtnAnnualInDefault
  }
  if (code === 'addon-ctn-bninat-annual') {
    const isCtnMonthlyInCart = centersAndAppsInCart.some(
      ({code: c}) => c === 'addon-ctn-bninat-m2m',
    )
    const isCtnMonthlyInDefault = defaultCentersAndApps?.apps?.some(
      ({code: c}) => c === 'addon-ctn-bninat-m2m',
    )
    isDisabled = isCtnMonthlyInCart || isCtnMonthlyInDefault
  }

  if (
    code.includes('mc_') &&
    isThryvLeadsInCart &&
    isNewTLMCAccessible &&
    !isAppAlreadyIncluded
  ) {
    isDisabled = false
  }

  if (code.includes('mc_') && isThryvLeadsInCart && !isNewTLMCAccessible) {
    isDisabled = true
  }

  if (code === 'mc_plus-5-thryv-m2m') {
    if (isMCProInDefault) isDisabled = true
  }

  // AU/NZ: disable MC 2.0 plan cards that would be a downgrade, unless the
  // user has MC_Downgrade_Access (FULSAS-12280 / BUSREQ-14036 — Admin-only
  // at launch, configurable via the same features-table flag checked
  // server-side in order-service.js#assertDowngradeAccessAllowed). This
  // block predates BUSREQ-14036 and previously disabled the card
  // unconditionally for every role, which would have made the Admin-only
  // downgrade capability unreachable in the UI.
  const isAUNZMC20Flag =
    (selectedCountry === 'AU' && MC_AU) || (selectedCountry === 'NZ' && MC_NZ)
  if (MC_2_0_PLANS.includes(code) && isAUNZMC20Flag && !MC_Downgrade_Access) {
    const existingMC20Plan = defaultCentersAndApps.centers.find(({code: c}) =>
      MC_2_0_PLANS.includes(c),
    )
    if (existingMC20Plan && existingMC20Plan.productRank > (productRank || 0)) {
      isDisabled = true
    }
  }

  if (code.includes('tl-')) {
    if (
      (isMCInCart ||
        isMCFoundationInCart ||
        isMCSigPremierInCart ||
        (isTLAvailableWhenBCInCart && selectedCountry === 'US')) &&
      isNewTLMCAccessible
    ) {
      isDisabled = false
    } else if (
      (!isMCInCart || !isPaidMCTrialInCart) &&
      !isTLAvailableWhenBCInCart &&
      isNewTLMCAccessible &&
      selectedCountry === 'US'
    ) {
      isDisabled = true
    } else if (
      (!isMCInCart || !isPaidMCTrialInCart) &&
      isNewTLMCAccessible &&
      selectedCountry !== 'US'
    ) {
      isDisabled = true
    }
  }

  if (isNewTLMCAccessible && code.includes('tl-')) {
    let isTLAlreadyInCart = centersAndAppsInCart.find(({code: itemCode}) =>
      itemCode.includes('tl-'),
    )
    if (isTLAlreadyInCart) {
      isDisabled = true
    }
  }

  if (
    ['addon-5-mcstartgr', 'addon-5-mcexpangr'].includes(code) &&
    isThryvLeadsInCart
  ) {
    isDisabled = true
  }

  // FULSAS-13425: Geo Add-On (Thryv Geo Expansion) must stay
  // selectable/reopenable — including tiers already owned, so quantity can
  // still go up — regardless of the generic isAppAlreadyIncluded/isInCart/
  // isExcluded/role baseline above. Thryv Leads and Geo are not mutually
  // exclusive (they may coexist in the cart), so nothing else gates this.
  if (GEO_ADDON_CODES.includes(code)) {
    isDisabled = false
  }

  if (
    ['bc-6-thryv-m2m'].includes(code) &&
    centersAndAppsInCart.find(({code: itemCode}) =>
      KEAP_FAMILY_CODES.includes(itemCode),
    )
  ) {
    isDisabled = true
  }

  if (keapAccount) {
    let isMCRelatedCenterNApps = [
      'mc_plus-5-thryv-m2m',
      'mc_pro-5-thryv-m2m',
      'addon-5-mcstartgr',
      'addon-5-mcexpangr',
      isMCInCart ? 'tl-mc-5-thryv-m2m' : null,
      'seobase-5-thryv-m2m',
      // 'addon-socialadsbronze-5-m2m',
      // 'addon-socialadssilver-5-m2m',
      // 'addon-socialadsgold-5-m2m',
    ].includes(code)
    isDisabled = !isMCRelatedCenterNApps
  }

  if (code.includes('addon-socialads')) {
    if (
      isMCInCart ||
      isMCProEnhancedInCart ||
      isMCFoundationInCart ||
      isMCSigPremierInCart
    ) {
      isDisabled = false
    } else if (isSocialAdsAvailableWhenBCInCart && selectedCountry === 'US') {
      isDisabled = false
    } else {
      isDisabled = true
    }
  }
  if (code.includes('addon-5-ai-webbuilder') && isPaidMCTrialInCart) {
    isDisabled = false
  }
  if (code.includes('addon-5-leadslift-m2m')) {
    isDisabled = false
  }

  if (code === 'addon-5-leadslift-bni-m2m' && !isMCInCart) {
    isDisabled = true
  }

  if (KEAP_FAMILY_CODES.includes(code) && isSameKeapPlanInDefault) {
    isDisabled = true
  }

  const isKeapMaxAddonInDefault = defaultCentersAndApps?.apps?.some(({code}) =>
    [
      'addon-5-keap-addcontacts-m2m',
      'addon-5-keap-addusers-m2m',
      'addon-5-keap-textvoice-m2m',
      'addon-5-keap-addcontacts-annual',
      'addon-5-keap-addusers-annual',
      'addon-5-keap-textvoice-annual',
      'addon-5-keap-legacy-graphly-contacts-annual',
      'addon-5-keap-legacy-plusthis-premium-trans-annual',
      'addon-5-keap-legacy-graphly-contacts-m2m',
      'addon-5-keap-legacy-plusthis-premium-trans-m2m',
    ].includes(code),
  )

  if (
    [
      'addon-5-keap-addcontacts-m2m',
      'addon-5-keap-addusers-m2m',
      'addon-5-keap-textvoice-m2m',
      'addon-5-keap-addcontacts-annual',
      'addon-5-keap-addusers-annual',
      'addon-5-keap-textvoice-annual',
      'addon-5-keap-legacy-graphly-contacts-annual',
      'addon-5-keap-legacy-plusthis-premium-trans-annual',
      'addon-5-keap-legacy-graphly-contacts-m2m',
      'addon-5-keap-legacy-plusthis-premium-trans-m2m',
    ].includes(code) &&
    isKeapMaxAddonInDefault
  ) {
    isDisabled = false
  }

  // TGP-DOWNGRADE #10 (to revert: delete this if-block and the
  // isTgpStarterCode/isTgpSignatureCode imports).
  // Signature -> Starter is a downgrade: without Enable_TGP_Downgrade, Starter
  // is disabled once a Signature / Signature Plus plan is in the cart or on
  // the account. With the flag on, #6 below keeps TGP plans enabled instead.
  if (
    !Enable_TGP_Downgrade &&
    isTgpStarterCode(code) &&
    centersAndAppsInCart.some(({code: cartCode}) =>
      isTgpSignatureCode(cartCode),
    )
  ) {
    isDisabled = true
  }

  // ---- BEGIN TGP-DOWNGRADE #6 (to revert: delete up to the END marker) ----
  // Enable_TGP_Downgrade: no rule above may grey out an individual TGP plan.
  // Still disabled: the plan itself once it is in the cart/account, and the
  // whole TGP card when it is unavailable for the role or excluded by Thryv
  // Leads (every Marketing Center plan is disabled together there).
  const isTgpCardDisabledAsAWhole =
    isRoleUnableToSelectCenter ||
    (code.includes('mc_') && isThryvLeadsInCart && !isNewTLMCAccessible)
  if (
    Enable_TGP_Downgrade &&
    isTgpFamilyCode(code) &&
    !isInCart &&
    !isAppAlreadyIncluded &&
    !isTgpCardDisabledAsAWhole
  ) {
    isDisabled = false
  }
  // ---- END TGP-DOWNGRADE #6 ----

  console.log('isDisabled finally' + isDisabled)
  const handleSelect = () => {
    if (name === selectedPlan?.name) return
    if (isDisabled) {
      if (prereqMessage) toast.warning(prereqMessage)
      return
    }
    if (name === 'Professionally Designed Website')
      setIsWebsiteSettingsModalVisible(true)
    // Use existing quantity for Keap Additional Users in upgrade mode
    if (code === 'addon-5-keap-addusers-m2m' && keapAddUsersRecurlyQty > 0) {
      setQuantity(keapAddUsersRecurlyQty)
    } else {
      setQuantity(1)
    }
    if (
      code === 'addon-5-keap-addusers-annual' &&
      keapAddUsersRecurlyQtyAnnual > 0
    ) {
      setQuantity(keapAddUsersRecurlyQtyAnnual)
    } else {
      setQuantity(1)
    }
    if (code === 'keap_kcc-cert-5-fee-annual' && kccRecurlyOption > 0) {
      amount = amount / kccRecurlyOption
    }
    const autoSelectedOptions = MC_2_0_SIGPREMIERPLANS.includes(code)
      ? (entitlements ?? []).filter(ent => ent.code === 'ent-5-website')
      : []
    setSelectedOptions(autoSelectedOptions)
    setSelectedPlan({
      name,
      amount,
      img,
      entitlements,
      id,
      planType,
      groupTitle,
      excludes,
      requiresCenter,
      code,
      autoAddCenters,
      disableApps,
      disableCenters,
      parentProduct,
      productRank,
      productBillingType,
      downgrades,
      maxQty,
      ...(alternatePlanCodes?.length && {alternatePlanCodes}),
      ...(tier && {tier}),
    })
  }
  return (
    <PlanOptionContainer
      onClick={handleSelect}
      selected={name === selectedPlan?.name}
      isFree={isFree}
      isUnavailable={isDisabled}
      data-testid={`plan-option-${id}`}
    >
      <div className="d-flex align-items-center">
        <Radio
          labelClassName="m-0"
          toggled={name === selectedPlan?.name}
          disabled={isDisabled}
          handleOnChange={handleSelect}
        />
        <img
          src={img}
          alt="plan logo"
          height={32}
          width={32}
          className="logo"
        />
        <div>
          <p
            className="m-0"
            style={{
              color: isDisabled && '#A3A5A7',
            }}
          >
            {name}
          </p>
          {subtitle && (
            <p
              className="m-0"
              style={{
                color: isDisabled ? '#A3A5A7' : '#808080',
                fontSize: '12px',
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <p className="m-0 dark-text">
        {isSEO
          ? 'Varies'
          : code === 'addon-5-leadslift-m2m'
          ? // Catalog list shows the base unit price ($1.00). Tier pricing
            // (amountToQuantityMap) only applies once a budget is picked in
            // Options, so bypass formatPrice's Leads Lift quantity lookup here.
            currencyFormat(amount ?? 0, selectedCountry)
          : formatPrice(
              amount ?? 0,
              code === 'addon-5-keap-addusers-m2m'
                ? quantity || keapAddUsersRecurlyQty || 1
                : code === 'addon-5-keap-addusers-annual'
                ? quantity || keapAddUsersRecurlyQtyAnnual || 1
                : code === 'addon-ctn-bninat-m2m'
                ? 1
                : code === 'addon-ctn-bninat-annual'
                ? 1
                : 1,
              name,
              selectedCountry,
            )}
      </p>
    </PlanOptionContainer>
  )
}

function CentersAndApps({
  data,
  selectedPlan,
  setSelectedPlan,
  selectedCentersAndApps,
  defaultCentersAndApps,
  setSelectedOptions,
  setQuantity,
  isUpgrade,
  isSEOMBGDisabled,
  setIsWebsiteSettingsModalVisible,
  selectedCountry,
  checkLegacySEO,
  accountSubscriptionData,
  hasBusinessCenterAccess,
  hasMarketingCenterAccess,
  hasKeapMaxAccess,
  hasKeapHVACAccess,
  isNewTLMCAccessible,
  hasCustomDataFYAccess,
  // hasCustomDataServiceAccess,
  keapAccount = false,
  keapAddUsersRecurlyQty = 0,
  keapAddUsersRecurlyQtyAnnual = 0,
  expiredCentersAndApps,
  kccRecurlyOption,
  upgradePaymentPreview,
}) {
  const userFeatures = useUserFeatures()
  // Derive existing CTN qty from alreadyIncluded apps (used in Options panel as minQuantity).
  const ctnRecurlyQty =
    defaultCentersAndApps?.apps?.find(
      ({code, alreadyIncluded}) =>
        code === 'addon-ctn-bninat-m2m' && alreadyIncluded,
    )?.quantity || 0
  const ctnRecurlyQtyAnnual =
    defaultCentersAndApps?.apps?.find(
      ({code, alreadyIncluded}) =>
        code === 'addon-ctn-bninat-annual' && alreadyIncluded,
    )?.quantity || 0
  const {roles = []} = useUserRoles()
  const isPartnerOpsUser = roles.includes('PartnerOps')
  // isKeapFrequencyLocked is not being checked by the uiGroup === "Keap"
  // because there are Legacy plans that comes as uiGrup as "Hidden"
  const isKeapFrequencyLocked =
    isUpgrade &&
    defaultCentersAndApps.centers.some(center =>
      center.code.startsWith('keap_'),
    )
  const hasExistingAnnualKeap =
    isUpgrade &&
    defaultCentersAndApps.centers.some(
      ({groupTitle, productBillingType, code}) =>
        (groupTitle === 'Keap' &&
          productBillingType?.toLowerCase() === 'annual') ||
        (groupTitle === 'Hidden' &&
          productBillingType?.toLowerCase() === 'annual' &&
          code.startsWith('keap_')),
    )
  const [keapBillingFrequency, setKeapBillingFrequency] = useState(
    hasExistingAnnualKeap ? 'annual' : 'monthly',
  )
  const hasExistingAnnualCtn =
    isUpgrade &&
    defaultCentersAndApps.apps.some(
      ({code, alreadyIncluded}) =>
        code === 'addon-ctn-bninat-annual' && alreadyIncluded,
    )
  const [thryvAddonsBillingFrequency, setThryvAddonsBillingFrequency] =
    useState(hasExistingAnnualCtn ? 'annual' : 'monthly')

  const billingFrequencyStatus = React.useMemo(() => {
    if (!isKeapFrequencyLocked) return keapBillingFrequency
    // const existingKeapPlan = defaultCentersAndApps.centers.find(({code}) =>
    //   KEAP_FAMILY_CODES.includes(code),
    // )

    return hasExistingAnnualKeap ? 'annual' : 'monthly'
  }, [
    isKeapFrequencyLocked,
    defaultCentersAndApps.centers,
    keapBillingFrequency,
  ])

  const isUserEnabledForBC5NewSales = userFeatures?.Bc5newsales
  const isUserEnabledForMCFreeTrial = userFeatures?.MC_Free_Trial
  const isUserEnabledForMCAU = userFeatures?.MC_AU
  const isUserEnabledForMCNZ = userFeatures?.MC_NZ
  // GEP-7133 / FULSAS-13599 (MC 5.0 -> TGP upgrade): see the "MC 5.0 -> TGP
  // upsell visibility override" doc comment further below, near the
  // MC_2_0_PLANS filter, for what this flag does and how to revert it.
  // Market-split per country, mirroring MC_AU/MC_NZ/MC_Free_Trial above —
  // AU/NZ and US are enabled independently so US can stay off while AU/NZ
  // are on (per the epic's rollout order).
  const isEnabledForAUNZTgpUpgrade = userFeatures?.Enable_AU_NZ_TGP_Upgrade
  const isEnabledForUSTgpUpgrade = userFeatures?.Enable_US_TGP_Upgrade
  const isTgpUpgradeEnabled =
    (['AU', 'NZ'].includes(selectedCountry) && isEnabledForAUNZTgpUpgrade) ||
    (selectedCountry === 'US' && isEnabledForUSTgpUpgrade)
  const hadInactiveMCFreeTrial = expiredCentersAndApps?.centers?.some(
    ({code}) => code === 'mc_freetrial-5-thryv-m2m',
  )
  const restrictedGroupKeys = React.useMemo(
    () =>
      parseRestrictedGroupsFromFeatures({
        userFeatures,
        selectedCountry,
        roles,
      }),
    [userFeatures, selectedCountry, roles],
  )
  console.log({keapAccount})

  const [visible, setVisible] = useState(false)
  const [openGroup, setOpenGroup] = useState(null) // Move openGroup state here
  const disableApps = Array.from(
    new Set(
      [
        ...selectedCentersAndApps.apps,
        ...selectedCentersAndApps.centers,
        ...selectedCentersAndApps.oneTimes,
      ].reduce((acc, plan) => {
        if (plan?.disableApps?.length) acc.push(...plan.disableApps)
        return acc
      }, []),
    ),
  )
  console.log('disableApps :', disableApps)
  const disableCenters = Array.from(
    new Set(
      [
        ...selectedCentersAndApps.apps,
        ...selectedCentersAndApps.centers,
        ...selectedCentersAndApps.oneTimes,
      ].reduce((acc, plan) => {
        if (plan?.disableCenters?.length) acc.push(...plan.disableCenters)
        return acc
      }, []),
    ),
  )
  console.log('disableCenters :', disableCenters)
  if (!data?.apps) data = {...data, apps: []}
  if (
    data.apps.findIndex(ele => {
      return ele.code === 'addon-5-seopb-m2m'
    }) >= 0
  )
    data.apps.splice(
      data.apps.findIndex(ele => {
        return ele.code === 'addon-5-seopb-m2m'
      }),
      1,
    )
  if (
    data.apps.findIndex(ele => {
      return ele.code === 'addon-5-seogeo-m2m'
    }) >= 0
  )
    data.apps.splice(
      data.apps.findIndex(ele => {
        return ele.code === 'addon-5-seogeo-m2m'
      }),
      1,
    )
  if (
    data.apps.findIndex(ele => {
      return ele.code === 'addon-5-seokey-m2m'
    }) >= 0
  )
    data.apps.splice(
      data.apps.findIndex(ele => {
        return ele.code === 'addon-5-seokey-m2m'
      }),
      1,
    )

  if (
    data.apps.findIndex(ele => {
      return ele.code === 'addon-5-pr-emp_wc'
    }) >= 0
  )
    data.apps.splice(
      data.apps.findIndex(ele => {
        return ele.code === 'addon-5-pr-emp_wc'
      }),
      1,
    )
  if (
    data.apps.findIndex(ele => {
      return ele.code === 'addon-5-pr-con_wc'
    }) >= 0
  )
    data.apps.splice(
      data.apps.findIndex(ele => {
        return ele.code === 'addon-5-pr-con_wc'
      }),
      1,
    )

  if (
    data.apps.findIndex(ele => {
      return ele.code === 'addon-6-seats-m2m'
    }) >= 0
  )
    data.apps.splice(
      data.apps.findIndex(ele => {
        return ele.code === 'addon-6-seats-m2m'
      }),
      1,
    )
  console.log('apps : ', data.apps)
  const defaultFreePlan = defaultCentersAndApps?.centers?.find(
    center => center.name.includes('Basic') && !isThryvBasicCode(center.code),
  )
  const centersAndAppsInCart = [
    ...selectedCentersAndApps.centers,
    ...selectedCentersAndApps.apps,
    ...selectedCentersAndApps.oneTimes,
  ]

  const hasKeapPlanInCart = centersAndAppsInCart.some(({code}) =>
    code?.includes('keap'),
  )
  const isGroupPrereqDisabled = group => {
    if (group.group === 'Keap Add-Ons') return !hasKeapPlanInCart
    if (group.group === 'Thryv Add-Ons') return false
    const hasItemInCart = group.items?.some(item =>
      centersAndAppsInCart.some(({code}) => code === item.code),
    )
    if (hasItemInCart) return false
    return (
      (group.items || []).length > 0 &&
      group.items.every(item => item.prereqDisabled)
    )
  }
  const isSelectedPlanSEO = ['seombg-5-thryv-m2m', 'seo-5-thryv-m2m'].includes(
    selectedPlan?.code,
  )
  // console.log({selectedCentersAndApps})
  // const isThryvLeadsIncluded = selectedCentersAndApps.center.find(
  //   item => item.name === 'Thryv Leads',
  // )

  const isRequiredCenterAndAddonInCart = (requiredCenters, requiredAddons) => {
    if (requiredCenters.length === 0 && requiredAddons.length === 0) return true
    let isCenterPresent = false
    let isAddonPresent = false
    const hasAnnualKeapInCart = centersAndAppsInCart.some(
      item =>
        item.productBillingType === 'Annual' && item.groupTitle === 'Keap',
    )

    const keapEquivalentCodes = hasAnnualKeapInCart
      ? data.centers.filter(c => c.groupTitle === 'Keap').map(c => c.code)
      : []

    for (const item of centersAndAppsInCart) {
      const codesToCheck = [item.code, ...keapEquivalentCodes]
      if (
        requiredCenters.length > 0 &&
        codesToCheck.some(c => requiredCenters.includes(c))
      ) {
        isCenterPresent = true
      }
      if (
        codesToCheck.some(c => requiredAddons.includes(c)) ||
        (item.entitlements &&
          item.entitlements.some(entitlement =>
            requiredAddons.includes(entitlement.code),
          ))
      ) {
        isAddonPresent = true
      }
      if (
        requiredAddons.length === 0 &&
        requiredCenters.length > 0 &&
        isCenterPresent
      ) {
        return true
      }
      if (requiredCenters.length > 0 && isCenterPresent && isAddonPresent) {
        return true
      }
      if (
        requiredAddons.length > 0 &&
        requiredCenters.length === 0 &&
        isAddonPresent
      ) {
        return true
      }
    }
    return false
  }
  const isNotEmpty = array => {
    if (array?.length > 0) {
      return true
    }
    return false
  }

  const groupAndSort = (array, groupKey, groupOrderKey, itemOrderKey) => {
    // Group by groupKey
    const grouped = array.reduce((result, item) => {
      ;(result[item[groupKey]] = result[item[groupKey]] || []).push(item)
      return result
    }, {})
    // Convert to array for sorting groups + sort items within group
    let groupedArray = Object.keys(grouped).map(key => ({
      group: key,
      groupSequence: grouped[key][0][groupOrderKey],
      groupIcon: GROUP_ICONS.find(icons => icons.name === key)?.img,
      items: grouped[key].sort((a, b) => a[itemOrderKey] - b[itemOrderKey]),
    }))
    // Sort groups: use CUSTOM_GROUP_ORDER when defined, otherwise fall back to groupSequence
    groupedArray.sort((a, b) => a.groupSequence - b.groupSequence)
    return groupedArray
  }

  // Memoize sortedGroupData to prevent unnecessary recalculations
  const sortedGroupData = React.useMemo(() => {
    // All your existing filtering and sorting logic
    // Filter centers
    // Filter Apps
    // Filter OneTimeFees
    // Merge all into a single arrary , Merged array
    // Grouped JSON based on uigroup
    // Grouped JSON with sorted groups based on group sequence and product sequence

    const allAccountSubs = [
      ...(accountSubscriptionData?.parentSubscriptions || []),
      ...(accountSubscriptionData?.childSubscriptions || []),
    ]
    const isFreeAccount = allAccountSubs.every(sub =>
      FREE_ACCOUNT_CODES.includes(sub.code),
    )
    const hasActiveMCFreeTrial = defaultCentersAndApps.centers.some(
      ({code}) => code === 'mc_freetrial-5-thryv-m2m',
    )

    let centersProds = (data?.centers || []).filter(center => {
      const hasBCPlan = defaultCentersAndApps.centers.some(({code}) =>
        [
          'bc_plus-5-thryv-m2m',
          'bc_pro-5-thryv-m2m',
          'bc_unlm-5-thryv-m2m',
          'bc-6-thryv-m2m',
        ].includes(code),
      )
      if (
        hasBCPlan &&
        (center?.code === 'bc_ent-6-thryv-m2m' ||
          center?.code === 'bc_ent-unlm-5-thryv-m2m')
      ) {
        return false
      }
      const isKeapCenter =
        center?.groupTitle === 'Keap' || center?.groupTitle === 'Keap Add-Ons'
      if (isKeapCenter) {
        if (
          billingFrequencyStatus === 'annual' &&
          (center?.productBillingType === 'Monthly' ||
            center?.productBillingType == null)
        ) {
          return false
        }
        if (
          billingFrequencyStatus !== 'annual' &&
          center?.productBillingType === 'Annual'
        ) {
          return false
        }
      } else {
        if (!visible) {
          if (center?.productBillingType === 'Annual') {
            return false
          }
        } else {
          if (center?.productBillingType === 'Monthly') {
            return false
          }
        }
      }
      const hasRequiredFlag = isRequiredFlagEnabled(
        userFeatures,
        center?.requiresFlags,
      )
      const hasRestrictFlag = isRestrictFlagEnabled(
        userFeatures,
        center?.restrictFlags,
      )
      if (hasRestrictFlag && !hasBCPlan) return false
      if (!hasRequiredFlag) return false
      const hasNeededFlag = checkIfHasNeededFlag(center?.needFlag, userFeatures)
      if (!hasNeededFlag) return false
      const centerAddonRequires = [
        ...(center?.requires || []),
        ...(center?.requiresAddon || []),
      ]
      const isRequiredItemInCart =
        (isNotEmpty(centerAddonRequires) ||
          isNotEmpty(center?.requiresCenter)) &&
        isRequiredCenterAndAddonInCart(
          center.requiresCenter,
          centerAddonRequires,
        )
      const hasMCProEnhanced = defaultCentersAndApps.centers.some(
        ({code}) => code === 'mc_pro_enh-5-thryv-m2m',
      )
      const hasMCTrialPlan = defaultCentersAndApps.centers.some(({code}) =>
        MC_TRIAL_PLAN_CODES.includes(code),
      )
      const hasMCPlan = defaultCentersAndApps.centers.some(({code}) =>
        ['mc_pro-5-thryv-m2m', 'mc_plus-5-thryv-m2m'].includes(code),
      )
      //===========================================//
      //  code added to hide MC Pro Enhanced  as it's as per requirement in case its need revert then remove or comment the code

      if (!hasMCProEnhanced && center.code === 'mc_pro_enh-5-thryv-m2m') {
        return false
      }
      //===========================================//
      const isMCPlan = ['mc_pro-5-thryv-m2m', 'mc_plus-5-thryv-m2m'].includes(
        center.code,
      )
      const isMCProEnhanced = center.code === 'mc_pro_enh-5-thryv-m2m'

      let parentSubscriptionsTotal = 0,
        childSubscriptionsTotal = 0

      if (
        accountSubscriptionData?.parentSubscriptions &&
        accountSubscriptionData?.parentSubscriptions.length > 0
      ) {
        accountSubscriptionData?.parentSubscriptions.forEach(
          item =>
            (parentSubscriptionsTotal = parentSubscriptionsTotal + item.total),
        )
      }
      if (
        accountSubscriptionData?.childSubscriptions &&
        accountSubscriptionData?.childSubscriptions.length > 0
      ) {
        accountSubscriptionData?.childSubscriptions.forEach(
          item =>
            (childSubscriptionsTotal = childSubscriptionsTotal + item.total),
        )
      }
      if (center.code === 'seo-5-thryv-m2m') {
        if (checkLegacySEO) {
          if (parentSubscriptionsTotal + childSubscriptionsTotal > 0) {
            return true
          } else {
            return false
          }
        }
      }
      if (
        (isNotEmpty(centerAddonRequires) ||
          isNotEmpty(center?.requiresCenter)) &&
        !isRequiredItemInCart
      ) {
        return false
      }
      // Thryv Basic (FULSAS-13789) is a TGP plan, not a legacy free "Basic".
      if (center.name.includes('Basic') && !isThryvBasicCode(center.code)) {
        return center.name === defaultFreePlan?.name
      }
      if (center.code === '247support-m2m') return false
      if (hasMCPlan && isMCProEnhanced) return false
      if (hasMCProEnhanced && isMCPlan) return false
      if (
        center.code === 'rc_plus-5-thryv-m2m' ||
        center.code === 'rc_basic-5-thryv-m2m'
      )
        return false

      //Business center 6.0 changes
      const hasBC5Plan = defaultCentersAndApps.centers.some(({code}) =>
        [
          'bc_plus-5-thryv-m2m',
          'bc_pro-5-thryv-m2m',
          'bc_unlm-5-thryv-m2m',
        ].includes(code),
      )
      const hasKeapMax = selectedCentersAndApps.centers.some(({code}) =>
        KEAP_FAMILY_CODES.includes(code),
      )
      if (
        (center.code === 'bc_plus-5-thryv-m2m' ||
          center.code === 'bc_pro-5-thryv-m2m' ||
          center.code === 'bc_unlm-5-thryv-m2m') &&
        !hasBC5Plan &&
        !isUserEnabledForBC5NewSales
      )
        return false
      const allAccountSubs = [
        ...(accountSubscriptionData?.parentSubscriptions || []),
        ...(accountSubscriptionData?.childSubscriptions || []),
      ]
      const isFreeAccount = allAccountSubs.every(sub =>
        FREE_ACCOUNT_CODES.includes(sub.code),
      )

      if (
        center.code === 'mc_pro-5-thryv-m2m' ||
        center.code === 'mc_plus-5-thryv-m2m'
      ) {
        const hasMC20PlanInAccount = [
          ...defaultCentersAndApps.centers,
          ...(defaultCentersAndApps.apps || []),
        ].some(({code}) => MC_2_0_PLANS.includes(code))
        if (!['US', 'AU', 'NZ'].includes(selectedCountry)) {
          if (hasMC20PlanInAccount) return false
          return true
        } else {
          const isCountryEnabled = {
            US: isUserEnabledForMCFreeTrial,
            AU: isUserEnabledForMCAU,
            NZ: isUserEnabledForMCNZ,
          }[selectedCountry]
          if (isCountryEnabled) {
            if (isPartnerOpsUser) {
              if (hasMC20PlanInAccount) return false
              return true
            }
            if (hasMCTrialPlan || hadInactiveMCFreeTrial) {
              return false
            }
            if (isFreeAccount) {
              return false
            }
          } else {
            if (hasMC20PlanInAccount) return false
            return true
          }
        }
      }

      // FULSAS-13789 (supersedes the FULSAS-13348 paid-MC-2.0 + VAS gate):
      // mc_foundation (TGP Foundation) only renders when the user holds
      // TGP_Foundation_Catalog_Enabled (see the matching check in PlanOption
      // below). Excluding it here too — instead of leaving it to PlanOption
      // alone — keeps it out of `filteredProducts`, so groupAndSort doesn't
      // create a group with nothing renderable in it.
      if (
        center.code === TGP_FOUNDATION_CODE &&
        !isTgpFoundationCatalogEnabled(userFeatures)
      ) {
        return false
      }
      // FULSAS-13789: Thryv Basic is gated by Thryv_Basic_Catalog_Enabled.
      if (
        isThryvBasicCode(center.code) &&
        !isThryvBasicCatalogEnabled(userFeatures)
      ) {
        return false
      }

      // FULSAS-10255: GA rollout – show Thryv 2.0 plans when the account
      // either (a) already has / had a free-trial (upgrade path), or
      // (b) has no paid subscriptions at all (net-new order).
      // Hide them only when the account has existing paid plans outside
      // FREE_ACCOUNT_CODES and no free-trial history.
      if (MC_2_0_PLANS.includes(center.code)) {
        if (hasMCProEnhanced) return false
        const isCountryEnabledForMC = {
          US: isUserEnabledForMCFreeTrial,
          AU: isUserEnabledForMCAU,
          NZ: isUserEnabledForMCNZ,
        }[selectedCountry]
        if (!isCountryEnabledForMC) return false
        if (isFreeAccount || hasMCTrialPlan || hadInactiveMCFreeTrial)
          return true
        const hasMC2Plan = [
          ...(accountSubscriptionData?.parentSubscriptions || []),
          ...(accountSubscriptionData?.childSubscriptions || []),
        ].some(({code}) => MC_2_0_PLANS.includes(code))
        return hasMC2Plan
      }
      if (center.code === 'bc-6-thryv-m2m' && (hasBC5Plan || hasKeapMax))
        return false
      if (
        center.upgradeGated &&
        ((selectedCountry === 'US' && isUserEnabledForMCFreeTrial) ||
          (selectedCountry === 'AU' && isUserEnabledForMCAU) ||
          (selectedCountry === 'NZ' && isUserEnabledForMCNZ))
      ) {
        const gatedCountries = center.upgradeGatedCountries
          ? center.upgradeGatedCountries.split(',').map(s => s.trim())
          : []
        const showCodes = center.showWhenCartHas
          ? center.showWhenCartHas.split(',').map(s => s.trim())
          : []
        const hideCodes = center.hideWhenCartHas
          ? center.hideWhenCartHas.split(',').map(s => s.trim())
          : []
        const isCountryInScope =
          !gatedCountries.length || gatedCountries.includes(selectedCountry)
        if (isCountryInScope) {
          const cartCodes = centersAndAppsInCart.map(({code}) => code)
          if (hideCodes.some(c => cartCodes.includes(c))) return false
          if (showCodes.some(c => cartCodes.includes(c))) return true
          if (!isUpgrade || isFreeAccount || hasActiveMCFreeTrial) return false
        }
      }
      return true
    })

    const isBc6Standalone =
      centersAndAppsInCart.some(({code}) => code === BC_6_CODE) &&
      !centersAndAppsInCart.some(({code}) => MC_CENTER_CODES.includes(code)) &&
      !centersAndAppsInCart.some(({code}) => OTHER_BC_CODES.includes(code))
    let apps = (data?.apps || []).filter(
      ({
        code,
        groupTitle,
        requires = [],
        requiresCenter = [],
        requiresAddon = [],
        requiresFlags = [],
        restrictFlags = [],
        needFlag,
        productBillingType,
        upgradeGated,
        upgradeGatedCountries,
        showWhenCartHas,
        hideWhenCartHas,
      }) => {
        const isKeapApp = groupTitle === 'Keap' || groupTitle === 'Keap Add-Ons'
        if (isKeapApp) {
          if (
            billingFrequencyStatus === 'annual' &&
            (productBillingType === 'Monthly' || productBillingType == null)
          ) {
            return false
          }
          if (
            billingFrequencyStatus !== 'annual' &&
            productBillingType === 'Annual'
          ) {
            return false
          }
        }
        if (groupTitle === 'Thryv Add-Ons') {
          const resolvedBillingType =
            productBillingType ||
            (code.endsWith('m2m')
              ? 'Monthly'
              : code.endsWith('annual')
              ? 'Annual'
              : null)
          if (
            thryvAddonsBillingFrequency === 'annual' &&
            resolvedBillingType === 'Monthly'
          ) {
            return false
          }
          if (
            thryvAddonsBillingFrequency !== 'annual' &&
            resolvedBillingType === 'Annual'
          ) {
            return false
          }
        }
        const appAddonRequires = [...requires, ...requiresAddon]
        const hasNeededFlag = checkIfHasNeededFlag(needFlag, userFeatures)
        if (!hasNeededFlag) return false

        if (
          code === LEADS_LIFT_CODE &&
          !getAuthByRole(LEADS_LIFT_ELIGIBLE_ROLES, roles)
        ) {
          return false
        }

        // codes have no need_flag on their JSON catalog entries, so gate explicitly.
        if (GEO_ADDON_CODES.includes(code) && !userFeatures.Enable_Geo_Addon) {
          return false
        }

        const eligibleListingRefreshCountries = ['US']
        if (
          code === LISTING_REFRESH_CODE &&
          !eligibleListingRefreshCountries.includes(selectedCountry)
        ) {
          return false
        }
        if (
          groupTitle === 'Thryv Success Services' &&
          roles.length === 1 &&
          roles[0] === 'PartnerOps'
        ) {
          return false
        }
        if (code === LISTING_REFRESH_CODE && isBc6Standalone) {
          return false
        }
        if (
          upgradeGated &&
          ((selectedCountry === 'US' && isUserEnabledForMCFreeTrial) ||
            (selectedCountry === 'AU' && isUserEnabledForMCAU) ||
            (selectedCountry === 'NZ' && isUserEnabledForMCNZ))
        ) {
          const gatedCountries = upgradeGatedCountries
            ? upgradeGatedCountries.split(',').map(s => s.trim())
            : []
          const showCodes = showWhenCartHas
            ? showWhenCartHas.split(',').map(s => s.trim())
            : []
          const hideCodes = hideWhenCartHas
            ? hideWhenCartHas.split(',').map(s => s.trim())
            : []
          const isCountryInScope =
            !gatedCountries.length || gatedCountries.includes(selectedCountry)
          if (isCountryInScope) {
            const cartCodes = centersAndAppsInCart.map(({code}) => code)
            if (hideCodes.some(c => cartCodes.includes(c))) return false
            if (showCodes.some(c => cartCodes.includes(c))) return true
            if (!isUpgrade || isFreeAccount || hasActiveMCFreeTrial)
              return false
          }
        }
        if (!isRequiredFlagEnabled(userFeatures, requiresFlags)) return false
        if (isRestrictFlagEnabled(userFeatures, restrictFlags)) return false
        if (isPartnerOpsUser) return true
        if (code === 'addon-5-visibilityboost') {
          if (isRequiredCenterAndAddonInCart(requiresCenter, requiresAddon))
            return true
          return isRequiredCenterAndAddonInCart(requiresCenter, [])
        }
        return isRequiredCenterAndAddonInCart(requiresCenter, appAddonRequires)
      },
    )
    let oneTimeFees = (data?.oneTimes || []).filter(item => {
      const {
        requires = [],
        requiresCenter = [],
        requiresAddon = [],
        requiresFlags = [],
        restrictFlags = [],
        needFlag,
        upgradeGated,
        upgradeGatedCountries,
        showWhenCartHas,
        hideWhenCartHas,
      } = item
      const oneTimeAddonRequires = [...requires, ...requiresAddon]

      const hasNeededFlag = checkIfHasNeededFlag(needFlag, userFeatures)
      if (!hasNeededFlag) return false
      if (!isRequiredFlagEnabled(userFeatures, requiresFlags)) return false
      if (isRestrictFlagEnabled(userFeatures, restrictFlags)) return false

      // Thryv 2.0 Growth Platform — 5 Extra Website Pages (thryv5page) is
      // only shown to Growth Platform accounts that already own
      // Professionally Designed Website.
      if (item.code === 'thryv5page') {
        const hasGrowthPlatformPlan = defaultCentersAndApps.centers.some(
          ({code: c}) => MC_2_0_PLANS.includes(c),
        )
        if (hasGrowthPlatformPlan && selectedCountry === 'US') {
          const hasProfessionallyDesignedWebsite =
            defaultCentersAndApps.centers.some(
              ({code: c, entitlements}) =>
                c === 'addon-5-website_difm' ||
                (entitlements ?? []).some(ent => ent.code === 'ent-5-website'),
            ) ||
            [
              ...(defaultCentersAndApps.apps || []),
              ...(defaultCentersAndApps.oneTimes || []),
            ].some(({code: c}) => c === 'addon-5-website_difm')
          return isPartnerOpsUser || hasProfessionallyDesignedWebsite
        }
      }

      const hasRequiredCenterOrAddon = isRequiredCenterAndAddonInCart(
        requiresCenter,
        oneTimeAddonRequires,
      )

      if (!hasRequiredCenterOrAddon && !isPartnerOpsUser) return false

      if (
        item.code === 'addon-5-keap-custdfy-ot' &&
        centersAndAppsInCart.some(({code}) => KEAP_FAMILY_CODES.includes(code))
      ) {
        return !!hasCustomDataFYAccess
      }

      if (
        upgradeGated &&
        ((selectedCountry === 'US' && isUserEnabledForMCFreeTrial) ||
          (selectedCountry === 'AU' && isUserEnabledForMCAU) ||
          (selectedCountry === 'NZ' && isUserEnabledForMCNZ))
      ) {
        const gatedCountries = upgradeGatedCountries
          ? upgradeGatedCountries.split(',').map(s => s.trim())
          : []
        const showCodes = showWhenCartHas
          ? showWhenCartHas.split(',').map(s => s.trim())
          : []
        const hideCodes = hideWhenCartHas
          ? hideWhenCartHas.split(',').map(s => s.trim())
          : []
        const isCountryInScope =
          !gatedCountries.length || gatedCountries.includes(selectedCountry)
        if (isCountryInScope) {
          const cartCodes = centersAndAppsInCart.map(({code}) => code)
          if (hideCodes.some(c => cartCodes.includes(c))) return false
          if (showCodes.some(c => cartCodes.includes(c))) return true
          if (!isUpgrade || isFreeAccount || hasActiveMCFreeTrial) return false
        }
      }

      if (!requires.length) return true
      const cartCodes = centersAndAppsInCart.flatMap(({code, entitlements}) => [
        code,
        ...(entitlements || []).map(e => e.code),
      ])

      return isPartnerOpsUser || cartCodes.some(code => requires.includes(code))
    })

    if (isPartnerOpsUser) {
      const markPrereq = item => {
        const {requires = [], requiresCenter = [], requiresAddon = []} = item
        const prereqMet = isRequiredCenterAndAddonInCart(requiresCenter, [
          ...requires,
          ...requiresAddon,
        ])
        return prereqMet ? item : {...item, prereqDisabled: true}
      }
      apps = apps.map(markPrereq)
      oneTimeFees = oneTimeFees.map(markPrereq)
    }

    apps = apps.map(item => {
      if (item.code !== 'addon-5-visibilityboost' || item.prereqDisabled) {
        return item
      }
      const {requiresCenter = [], requiresAddon = []} = item
      const hasSignature = isRequiredCenterAndAddonInCart(requiresCenter, [])
      const prereqMet = isRequiredCenterAndAddonInCart(
        requiresCenter,
        requiresAddon,
      )
      if (hasSignature && !prereqMet) {
        return {
          ...item,
          prereqDisabled: true,
          prereqMessage:
            'Add a Professionally Designed Website to this account before selecting the AI Visibility Boost Add-On.',
        }
      }
      return item
    })

    let filteredProducts = [...centersProds, ...apps, ...oneTimeFees]
    console.log('filteredProducts : ', filteredProducts)
    filteredProducts = filteredProducts.filter(
      item => item.groupTitle != null && item.groupTitle != 'Hidden',
    )
    filteredProducts = filteredProducts.filter(
      item => !isGroupRestrictedByFeature(item.groupTitle, restrictedGroupKeys),
    )

    if (
      isUserEnabledForMCFreeTrial &&
      isFreeAccount &&
      selectedCountry === 'US'
    ) {
      filteredProducts = filteredProducts.filter(
        item => item.groupTitle !== 'Marketing Center',
      )
    }

    const result = groupAndSort(
      filteredProducts,
      'groupTitle',
      'groupSeq',
      'productSeq',
    )
    return result
  }, [
    visible,
    billingFrequencyStatus,
    thryvAddonsBillingFrequency,
    data,
    defaultCentersAndApps,
    selectedCentersAndApps,
    userFeatures,
    restrictedGroupKeys,
    roles,
    isPartnerOpsUser,
    hadInactiveMCFreeTrial,
    accountSubscriptionData?.parentSubscriptions,
    accountSubscriptionData?.childSubscriptions,
    centersAndAppsInCart,
    checkLegacySEO,
    defaultFreePlan?.name,
    hasCustomDataFYAccess,
    isPartnerOpsUser,
    isRequiredCenterAndAddonInCart,
    isUpgrade,
    isUserEnabledForBC5NewSales,
    isUserEnabledForMCAU,
    isUserEnabledForMCFreeTrial,
    isUserEnabledForMCNZ,
    isTgpUpgradeEnabled,
    selectedCountry,
  ])

  const Accordion = ({groups}) => {
    const hasMCTrialPlan = defaultCentersAndApps.centers.some(({code}) =>
      MC_TRIAL_PLAN_CODES.includes(code),
    )
    const allAccountSubs = [
      ...(accountSubscriptionData?.parentSubscriptions || []),
      ...(accountSubscriptionData?.childSubscriptions || []),
    ]
    const isFreeAccount = allAccountSubs.every(sub =>
      FREE_ACCOUNT_CODES.includes(sub.code),
    )
    const toggleGroup = groupName => {
      setOpenGroup(prev => (prev === groupName ? null : groupName))
    }
    const handleBillingToggle = newValue => {
      setVisible(newValue)
    }
    const handleKeapBillingFrequencyToggle = newValue => {
      setKeapBillingFrequency(newValue ? 'annual' : 'monthly')
      // If user is changing billing frequency, we should reset selected plan to avoid mismatch of plans and billing frequencies
      setSelectedPlan(null)
    }
    const handleThryvAddonsBillingToggle = newValue => {
      setThryvAddonsBillingFrequency(newValue ? 'annual' : 'monthly')
      setSelectedPlan(null)
    }

    console.log('Inside Accordion :: ', groups)
    return (
      <div className="accordion">
        {groups.map(group => {
          const groupDisabled = isPartnerOpsUser && isGroupPrereqDisabled(group)
          return (
            <div
              key={group.group}
              className="accordion-item"
              style={groupDisabled ? {opacity: 0.5} : undefined}
            >
              <div className="d-flex justify-content-left accordion-header">
                <img
                  src={group.groupIcon}
                  alt="plan logo"
                  height={32}
                  width={32}
                  className="groupLogo"
                />
                <button
                  onClick={() => !groupDisabled && toggleGroup(group.group)}
                  disabled={groupDisabled}
                  style={groupDisabled ? {cursor: 'not-allowed'} : undefined}
                >
                  {group.group}
                </button>
              </div>
              {!groupDisabled && openGroup === group.group && (
                <div className="accordion-content">
                  {['Thryv Growth Platform'].includes(group.group) &&
                    ((isUserEnabledForMCFreeTrial &&
                      (hasMCTrialPlan || isFreeAccount) &&
                      selectedCountry === 'US') ||
                      (selectedCountry === 'AU' &&
                        isUserEnabledForMCAU &&
                        (hasMCTrialPlan || isFreeAccount)) ||
                      (selectedCountry === 'NZ' &&
                        isUserEnabledForMCNZ &&
                        (hasMCTrialPlan || isFreeAccount))) && (
                      <div
                        style={{marginBottom: '16px', paddingLeft: '10px'}}
                        onClick={e => e.stopPropagation()}
                      >
                        <Toggle
                          label="Annual"
                          toggleSize="normal"
                          onChange={handleBillingToggle}
                          toggled={visible}
                          customSelectedColor="#16A085"
                          customUnselectedColor="#808080"
                        />
                      </div>
                    )}
                  {group.group === 'Keap Add-Ons' && (
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#808080',
                        paddingLeft: '10px',
                        marginBottom: '8px',
                      }}
                    >
                      Add-on billing frequency is locked to{' '}
                      <strong>
                        {billingFrequencyStatus === 'annual'
                          ? 'Annual'
                          : 'Monthly'}
                      </strong>{' '}
                      to match your Keap plan.
                    </p>
                  )}
                  {group.group === 'Keap' && isKeapFrequencyLocked && (
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#808080',
                        paddingLeft: '10px',
                        marginBottom: '8px',
                      }}
                    >
                      Billing frequency is locked to{' '}
                      <strong>
                        {billingFrequencyStatus === 'annual'
                          ? 'Annual'
                          : 'Monthly'}
                      </strong>{' '}
                      based on your existing Keap subscription.
                    </p>
                  )}
                  {group.group === 'Keap' && (
                    <div
                      style={{marginBottom: '16px', paddingLeft: '10px'}}
                      onClick={e => e.stopPropagation()}
                    >
                      <Toggle
                        label="Annual"
                        toggleSize="normal"
                        onChange={handleKeapBillingFrequencyToggle}
                        toggled={keapBillingFrequency === 'annual'}
                        customSelectedColor="#16A085"
                        customUnselectedColor="#808080"
                        disabled={isKeapFrequencyLocked}
                      />
                    </div>
                  )}
                  {group.group === 'Thryv Add-Ons' &&
                    group.items.some(
                      item =>
                        item.code === 'addon-ctn-bninat-m2m' ||
                        item.code === 'addon-ctn-bninat-annual',
                    ) && (
                      <div
                        style={{marginBottom: '16px', paddingLeft: '10px'}}
                        onClick={e => e.stopPropagation()}
                      >
                        <Toggle
                          label="Annual"
                          toggleSize="normal"
                          onChange={handleThryvAddonsBillingToggle}
                          toggled={thryvAddonsBillingFrequency === 'annual'}
                          customSelectedColor="#16A085"
                          customUnselectedColor="#808080"
                        />
                      </div>
                    )}
                  <ul>
                    {group.items.map((item, _, centers) => {
                      if (item.prodType === 'Center') {
                        return (
                          <PlanOption
                            key={item.id}
                            parentProduct={item.parentProduct}
                            name={item.name}
                            amount={item.amount}
                            quantity={item.quantity ?? 1}
                            img={item.img}
                            entitlements={item.entitlements}
                            id={item.id}
                            selectedPlan={selectedPlan}
                            setSelectedPlan={setSelectedPlan}
                            groupTitle={item.groupTitle}
                            excludes={item.excludes}
                            requiresCenter={item.requiresCenter}
                            code={item.code}
                            planType="center"
                            centersAndAppsInCart={centersAndAppsInCart}
                            defaultCentersAndApps={defaultCentersAndApps}
                            setSelectedOptions={setSelectedOptions}
                            setQuantity={setQuantity}
                            tier={item.tier}
                            isUpgrade={isUpgrade}
                            centers={centers}
                            isSEOMBGDisabled={isSEOMBGDisabled}
                            hasBusinessCenterAccess={hasBusinessCenterAccess}
                            hasMarketingCenterAccess={hasMarketingCenterAccess}
                            hasKeapMaxAccess={hasKeapMaxAccess}
                            hasKeapHVACAccess={hasKeapHVACAccess}
                            subtitle={
                              item.code === 'mc_pro_enh-5-thryv-m2m' &&
                              'For legacy upgrades only'
                            }
                            isNewTLMCAccessible={isNewTLMCAccessible}
                            accountSubscriptionData={accountSubscriptionData}
                            keapAccount={keapAccount}
                            autoAddCenters={item.autoAddCenters}
                            disableApps={item.disableApps}
                            disableCenters={item.disableCenters}
                            selectedCountry={selectedCountry}
                            productRank={item.productRank}
                            productBillingType={item.productBillingType}
                            downgrades={item.downgrades}
                            alternatePlanCodes={item.alternatePlanCodes}
                            keapAddUsersRecurlyQty={keapAddUsersRecurlyQty}
                            keapAddUsersRecurlyQtyAnnual={
                              keapAddUsersRecurlyQtyAnnual
                            }
                            kccRecurlyOption={kccRecurlyOption}
                          />
                        )
                      } else if (item.prodType === 'App') {
                        return (
                          <PlanOption
                            key={item.id}
                            name={item.name}
                            amount={item.amount}
                            quantity={item.quantity ?? 1}
                            img={item.img}
                            entitlements={item.entitlements}
                            id={item.id}
                            code={item.code}
                            selectedPlan={selectedPlan}
                            parentProduct={item?.parentProduct}
                            setSelectedPlan={setSelectedPlan}
                            groupTitle={item.groupTitle}
                            excludes={item.excludes}
                            requiresCenter={item.requiresCenter}
                            maxQty={item.maxQty}
                            planType="app"
                            centersAndAppsInCart={centersAndAppsInCart}
                            defaultCentersAndApps={defaultCentersAndApps}
                            setSelectedOptions={setSelectedOptions}
                            setQuantity={setQuantity}
                            setIsWebsiteSettingsModalVisible={
                              setIsWebsiteSettingsModalVisible
                            }
                            isNewTLMCAccessible={isNewTLMCAccessible}
                            accountSubscriptionData={accountSubscriptionData}
                            keapAccount={keapAccount}
                            disableApps={item.itemDisableApps}
                            disableCenters={item.itemDisableCenters}
                            isDisabled={
                              item.prereqDisabled ||
                              item.itemDisableApps?.includes(item.code) ||
                              item.itemDisableCenters?.includes(item.code)
                            }
                            prereqMessage={item.prereqMessage}
                            hasKeapMaxAccess={hasKeapMaxAccess}
                            hasKeapHVACAccess={hasKeapHVACAccess}
                            selectedCountry={selectedCountry}
                            productRank={item.productRank}
                            productBillingType={item.productBillingType}
                            keapAddUsersRecurlyQty={keapAddUsersRecurlyQty}
                            keapAddUsersRecurlyQtyAnnual={
                              keapAddUsersRecurlyQtyAnnual
                            }
                            ctnRecurlyQty={ctnRecurlyQty}
                            ctnRecurlyQtyAnnual={ctnRecurlyQtyAnnual}
                          />
                        )
                      } else if (item.prodType === 'OneTimeFees') {
                        return (
                          <PlanOption
                            key={item.id}
                            id={item.id}
                            name={item.name}
                            amount={item.amount}
                            quantity={item.quantity ?? 1}
                            code={item.code}
                            img={item.img}
                            parentProduct={item?.parentProduct}
                            centersAndAppsInCart={centersAndAppsInCart}
                            defaultCentersAndApps={defaultCentersAndApps}
                            planType="oneTime"
                            selectedPlan={selectedPlan}
                            setSelectedPlan={setSelectedPlan}
                            groupTitle={item.groupTitle}
                            setSelectedOptions={setSelectedOptions}
                            setQuantity={setQuantity}
                            isNewTLMCAccessible={isNewTLMCAccessible}
                            accountSubscriptionData={accountSubscriptionData}
                            keapAccount={keapAccount}
                            isDisabled={item.prereqDisabled}
                            disableApps={item.disableApps}
                            disableCenters={item.disableCenters}
                            hasKeapHVACAccess={hasKeapHVACAccess}
                            selectedCountry={selectedCountry}
                            keapAddUsersRecurlyQty={keapAddUsersRecurlyQty}
                            keapAddUsersRecurlyQtyAnnual={
                              keapAddUsersRecurlyQtyAnnual
                            }
                          />
                        )
                      }
                    })}
                  </ul>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  console.log('Accordion :: ', Accordion)
  return (
    <CatalogSectionContainer
      style={{
        padding: '17px 0 0 0',
        background: '#5378FC',
        borderRadius: '15px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingRight: '10px',
        }}
      >
        {/* <ParagraphText className="mb-3 font-weight-bold" variant="lg"> */}
        <Header variant="h4" fontWeight="semibold" className="softwareTitle">
          Software & Add-Ons
        </Header>
        <br></br>
        <br></br>
        {/* </ParagraphText> */}
        {selectedCountry === 'AU' && (
          <ParagraphText
            variant="sm"
            style={{color: '#4D4D4D', fontSize: '14px'}}
          >
            *All Plans include GST{' '}
            {!upgradePaymentPreview && isSelectedPlanSEO && 'except SEO'}
          </ParagraphText>
        )}
      </div>
      <div className="plans-container">
        <Accordion groups={sortedGroupData} />
      </div>
    </CatalogSectionContainer>
  )
}

export {CentersAndApps}
